/**
 * What each player input means, independent of the device it comes from.
 * A binding id names an action; its bindings list every physical input that
 * performs it, so keyboard, mouse and gamepad can all be bound at once.
 */

/** Combat and movement actions the game itself acts on. */
export const GAMEPLAY_INPUT_BINDING_IDS = [
    /** Contextual click: walk to ground, approach and attack an enemy, approach and use an interactable. */
    'primary-action',
    /** Swing the main hand at the cursor without moving. */
    'aimed-attack',
    /** Off-hand attack at the cursor. */
    'offhand-attack',
    /** Walk toward the cursor regardless of what is under it. */
    'force-move',
    'dodge',
] as const;

/** Actions the page UI handles (the game never sees these). */
export const UI_INPUT_BINDING_IDS = [
    'action-bar-1',
    'action-bar-2',
    'action-bar-3',
    'action-bar-4',
    'action-bar-5',
    'action-bar-6',
    'action-bar-7',
    'action-bar-8',
    'action-bar-9',
    /** Leave the run / open the in-run menu. */
    'open-menu',
    /** Slide the HUD's commission panel in or out. */
    'toggle-commission',
    /** Collapse or expand the HUD's map panel. */
    'toggle-map',
    /** Slide the HUD's carried inventory panel in or out. */
    'toggle-inventory',
    /** Slide the loadout's Essence Vessel out or back. */
    'toggle-vessel',
    /** Show the party HUD area's Party tab. */
    'show-party',
    /** Show the party HUD area's Chat tab and start typing a message. */
    'show-chat',
    /** Show or hide the run's combat log, the party HUD area's Log tab (#273). */
    'toggle-combat-log',
    /* Character Home tabs; Overview reuses toggle-commission, Inventory toggle-inventory. Pressing the open tab's key returns to Overview. */
    'open-skills',
    'open-crucibles',
    'open-crafting',
    'open-journey',
    /** Open the bug report form, in Character Home or a run (#218). */
    'report-bug',
    /** Developers only: save the tab as it looks, HUD and all (#303). */
    'take-snapshot',
    /** Developers only: save the 3D scene alone, without the HUD, at twice the window's size (#303). */
    'take-clean-render',
] as const;

/** Continuous direction inputs. */
export const ANALOG_INPUT_BINDING_IDS = ['move'] as const;

export type GameplayInputBindingId = typeof GAMEPLAY_INPUT_BINDING_IDS[number];
export type UiInputBindingId = typeof UI_INPUT_BINDING_IDS[number];
export type DigitalInputBindingId = GameplayInputBindingId | UiInputBindingId;
export type AnalogInputBindingId = typeof ANALOG_INPUT_BINDING_IDS[number];

/** Modifier keys, named as `KeyboardEvent.key` reports them. */
export const MODIFIER_KEYS = ['Shift', 'Control', 'Alt', 'Meta'] as const;
export type ModifierKey = typeof MODIFIER_KEYS[number];

/** A single on/off input. Keyboard keys are `KeyboardEvent.key` values; single characters match either case. */
export type DigitalInput =
    | { readonly device: 'keyboard'; readonly key: string }
    /** 0 = left, 1 = middle, 2 = right. */
    | { readonly device: 'mouse'; readonly button: number }
    /** Standard-mapping button index (0 = bottom face button, 9 = Start). */
    | { readonly device: 'gamepad'; readonly button: number };

export interface DigitalInputBinding {
    readonly kind: 'digital';
    readonly input: DigitalInput;
    /** Modifiers that must be held. A binding with more matching modifiers wins over one with fewer. */
    readonly modifiers?: readonly ModifierKey[];
}

/** A direction source. */
export type AnalogInput = { readonly device: 'gamepad'; readonly stick: 'left' | 'right' };

export interface AnalogInputBinding {
    readonly kind: 'analog';
    readonly input: AnalogInput;
}

export type InputBindings =
    & { readonly [Id in DigitalInputBindingId]: readonly DigitalInputBinding[] }
    & { readonly [Id in AnalogInputBindingId]: readonly AnalogInputBinding[] };
